<template>
  <div class="full-details-wrapper" :class="{ 'no-border': !showFindingsFrame }">
    <div v-if="showFindingsFrame" class="d-flex align-center px-4 pt-4">
      <div class="subtitle1 mb-4" data-testid="tickets-page-findings-title">
        {{ $t("ticketDetails.findings") }}
      </div>
    </div>

    <unified-device-dlp-expansion-panels
      v-if="!structureLoading"
      :findings-data="findingsData"
      :structure="structure"
      :show-last-seen="showLastSeen"
      @panel-expanded="handlePanelExpanded"
      @panel-collapsed="handlePanelCollapsed"
      @page-changed="handlePageChange"
    />
    <v-skeleton-loader v-else type="list-item-three-line"></v-skeleton-loader>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref } from "vue";
import {
  type DeviceDlpUnifiedFindingStructureItem,
  type FileFinding,
  useTicketStore,
} from "@/_store/tickets/ticket.module.ts";
import UnifiedDeviceDlpExpansionPanels from "@/components/tickets/ticket-preview-details/base-components/UnifiedDeviceDlpExpansionPanels.vue";
import type { TicketDetails } from "@/_store/tickets/tickets.module.ts";
import { AppContext } from "@/types.ts";
import { endpointDataGovernanceUnifiedTriggers, TicketTrigger } from "@/constants/tickets.ts";
import { handleVirtualScrollData } from "@coronet/cui";
import { sortFindingsGroupsByDataType } from "@/_helpers/ticket-helpers.ts";
import { useMspTicketsStore } from "@/_store/msp/tickets/msp-tickets.module.ts";

export interface UnifiedFileFindingsDataItem {
  loading: boolean;
  items: FileFinding[];
  total: number;
  page: number;
}

export type UnifiedFileFindingsData = Partial<Record<TicketTrigger, UnifiedFileFindingsDataItem>>;

const createDefaultFindingItem = (): UnifiedFileFindingsDataItem => ({
  loading: false,
  items: [],
  total: 0,
  page: 0,
});

export default defineComponent({
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
    context: {
      type: String as PropType<AppContext>,
      required: true,
    },
  },
  components: {
    UnifiedDeviceDlpExpansionPanels,
  },
  setup(props) {
    const ticketStore = useStoreModule();
    const structureLoading = ref(false);
    const { getUnifiedFileFindingsStructure, getUnifiedFileFindingsData } = ticketStore;

    const findingsData = ref<UnifiedFileFindingsData>({});

    const structure = ref<DeviceDlpUnifiedFindingStructureItem[]>([]);

    // Keep the header and border while findings load or exist; once we know the
    // ticket has no findings, collapse the section (hide title, drop the border).
    const showFindingsFrame = computed(() => structureLoading.value || structure.value.length > 0);

    const showLastSeen = computed(() =>
      endpointDataGovernanceUnifiedTriggers.includes(props.ticket.eventTrigger)
    );

    onMounted(async () => {
      try {
        structureLoading.value = true;
        const { data } = await getUnifiedFileFindingsStructure(props.ticket.eventId);
        structure.value = sortFindingsGroupsByDataType(data);
        // Seed a findings bucket per trigger the structure exposes
        findingsData.value = structure.value.reduce<UnifiedFileFindingsData>(
          (acc, { eventTrigger }) => {
            acc[eventTrigger] = createDefaultFindingItem();
            return acc;
          },
          {}
        );
      } catch (e) {
        console.error(e);
      } finally {
        structureLoading.value = false;
      }
    });

    function useStoreModule() {
      return props.context === AppContext.MSP ? useMspTicketsStore() : useTicketStore();
    }

    const handlePanelExpanded = async (eventTrigger: TicketTrigger) => {
      if (findingsData.value[eventTrigger]) {
        findingsData.value[eventTrigger].loading = true;
        const page = findingsData.value[eventTrigger].page;
        await fetchData(eventTrigger, page);
      }
    };

    const handlePanelCollapsed = (eventTrigger: TicketTrigger) => {
      if (findingsData.value[eventTrigger]) {
        findingsData.value[eventTrigger] = createDefaultFindingItem();
      }
    };

    const handlePageChange = async (eventTrigger: TicketTrigger) => {
      if (findingsData.value[eventTrigger]) {
        const newPage = findingsData.value[eventTrigger].page + 1;
        findingsData.value[eventTrigger].page = newPage;
        await fetchData(eventTrigger, newPage);
      }
    };

    const fetchData = async (eventTrigger: TicketTrigger, page: number) => {
      if (findingsData.value[eventTrigger]) {
        try {
          const { data } = await getUnifiedFileFindingsData(
            props.ticket.eventId,
            eventTrigger,
            page
          );
          const currentItems = findingsData.value[eventTrigger].items;
          findingsData.value[eventTrigger].items = handleVirtualScrollData(
            currentItems,
            data.items,
            "filePath"
          );
          findingsData.value[eventTrigger].total = data.total;
          findingsData.value[eventTrigger].loading = false;
        } catch (e) {
          console.error(e);
        }
      }
    };

    return {
      structure,
      findingsData,
      handlePanelExpanded,
      handlePanelCollapsed,
      handlePageChange,
      structureLoading,
      showFindingsFrame,
      showLastSeen,
    };
  },
});
</script>

<style scoped lang="scss">
.full-details-wrapper {
  border: 1px solid rgb(var(--v-theme-indigo-faint));

  &.no-border {
    border: none;
  }
}

.expansion-panel-header {
  height: 56px;
  background-color: rgb(var(--v-theme-indigo-faint));
  border-radius: 0 !important;
}

:deep(*) {
  .v-table__wrapper {
    border: 0 !important;
  }

  .v-expansion-panel--active:not(:first-child),
  .v-expansion-panel--active + .v-expansion-panel {
    margin-top: 0 !important;
  }
}
</style>
